import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
// SAN-712: needs .tz() for the viewer-timezone slot-time toggle below.
// Plain 'moment' happened to have .tz available elsewhere in this app only
// because some other file loaded earlier in the same bundle imports
// 'moment-timezone' (which extends the shared moment singleton) -- import
// it directly here instead of relying on that load-order assumption.
import * as moment from 'moment-timezone';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Subject, Observable, distinctUntilChanged, takeUntil, retry } from 'rxjs';
import { ICalenderEventModal } from 'src/app/core/domain/meetings.model';
import { IProfileDetails } from 'src/app/core/domain/profile.model';
import { MeetingService } from 'src/app/core/service/meeting.service';
import { getProfileData } from 'src/app/core/state/profile';
import { getGlobalSettings } from 'src/app/core/state/global';
import { IGlobalSettings } from 'src/app/core/domain/settings.model';
import { GlobalService } from 'src/app/core/service/global.service';
import 'add-to-calendar-button';
import { ProfileService } from 'src/app/core/service/profile.service';
import { formatEnumStringForUI, formatEventTypeLabel } from '../../utils/common-methods';
import Swal from 'sweetalert2';
import { ToastAlertService } from '../../utils/toast.service';
import lgZoom from 'lightgallery/plugins/zoom';
import { LightGalleryAllSettings } from 'lightgallery/lg-settings';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-event-details-one-on-one-modal',
  templateUrl: './event-details-one-on-one-modal.component.html',
  styleUrls: ['./event-details-one-on-one-modal.component.scss']
})
export class EventDetailsOneOnOneModalComponent implements OnInit {
  globalSettings: IGlobalSettings;
  settings: Partial<LightGalleryAllSettings> = {
    counter: false,
    plugins: [lgZoom],
    licenseKey: 'GPLv3',
  };

  @Input() modalInst: NgbModalRef;

  @Input() modalData: {
    action: string;
    event: any;
  };

  @Input() isRescheduling = false;
  bookingLoading = false;
  bookingLoadingForSlotTimeFrom;
  private destroyed$: Subject<void> = new Subject();
  public profileDetails$: Observable<IProfileDetails>;
  profileDetails: IProfileDetails;
  selectedResponse;
  // Typed `any`, not `any[]` -- app-attend-information's `eventQuestionList`
  // input is (pre-existingly) declared as the narrow empty-tuple type `[]`,
  // which only accepts an untyped/`any` value, not a real `any[]`.
  eventQuestionsList: any = [];
  eventUUID;
  bookingData: any = {};
  showChildComponent = false;
  attendingType="one_to_one";

  // SAN-768/BRD: inline, close-to-the-action error state for a failed apply
  // attempt (slot taken, per-startup limit reached, etc.) -- shown alongside
  // the existing toast rather than instead of it, since the toast alone
  // disappears before a user who looked away notices it. 'warning' is used
  // for an ordinary business-rule block (e.g. the startup's own slot limit)
  // vs 'danger' for a real conflict/failure, based on the backend's HTTP
  // status rather than string-matching its message text.
  applyErrorMessage: string = null;
  applyErrorSeverity: 'warning' | 'danger' = 'danger';

  eventQuestionsLoadError = false;

  // SAN-792/BRD FR-3.2: every live application (Applied/Approved/Rejected)
  // this startup has for this event, across whichever slots they're on --
  // replaces the old single-record model, which could only ever reflect
  // ONE application and hid the entire slot picker the moment any
  // application existed, making a per-event limit greater than 1 impossible
  // to use through this modal.
  myApplications: any[] = [];
  withdrawLoadingUuid: string = null;
  // SAN-711: event schedules are configured (and stored) in IST; slot times
  // rendered here carry no timezone info of their own, so label it
  // explicitly rather than leaving a bare "4:15 pm" ambiguous for a viewer
  // in another timezone.
  readonly eventTimeZoneLabel = 'India Standard Time (IST)';

  // SAN-712: matches EVENT_SCHEDULE_TIMEZONE in the backend
  // (events.service.ts) -- event.dates[]/slot times are wall-clock values
  // in this zone, not in the viewer's own timezone.
  private readonly eventTimeZone = 'Asia/Kolkata';
  readonly viewerTimeZone: string = moment.tz.guess();
  // Display-only toggle -- what actually gets booked is unchanged by this
  // (handleBook() still sends the raw event-zone slot value, same as
  // before); this only changes which zone slot times are *shown* in.
  showTimesInViewerZone = false;

  get isViewerInDifferentTimeZone(): boolean {
    return !!this.viewerTimeZone && this.viewerTimeZone !== this.eventTimeZone;
  }

  get viewerTimeZoneLabel(): string {
    if (!this.viewerTimeZone) return 'your local time';
    return moment.tz(this.viewerTimeZone).format('z') + ' (' + this.viewerTimeZone.replace(/_/g, ' ') + ')';
  }

  formatSlotTime(date: string, time: string): string {
    if (!time) return '';
    const displayZone = this.showTimesInViewerZone ? this.viewerTimeZone : this.eventTimeZone;
    const anchorDate = date || this.modalData?.event?.date;
    if (!anchorDate) {
      // No date to anchor a timezone conversion to (shouldn't happen for
      // a real slot) -- fall back to the un-converted event-zone value
      // rather than showing something wrong.
      return moment(time, 'HH:mm:ss').format('hh:mm A');
    }
    return moment.tz(`${anchorDate} ${time}`, 'YYYY-MM-DD HH:mm:ss', this.eventTimeZone)
      .tz(displayZone)
      .format('hh:mm A');
  }
  constructor(
    private route: ActivatedRoute,
    private modal: NgbModal,
    private meetingService: MeetingService,
    private profileService: ProfileService,
    private router: Router,
    private readonly store: Store,
    private uiLoader: NgxUiLoaderService,
    private globalService: GlobalService,
    private toastService: ToastAlertService
  ) { }

  ngOnInit(): void {
    this.eventUUID=this.modalData.event.uuid;
    if (this.modalData?.event?.checkIfResponseExists?.responseAction) {
      this.selectedResponse = { value: this.modalData.event?.checkIfResponseExists?.responseAction }
    }

    this.profileDetails$ = this.store.pipe(select(getProfileData));
    this.profileDetails$.pipe(distinctUntilChanged(), takeUntil(this.destroyed$)).subscribe((res) => {
      if (res) { this.profileDetails = res; }
    })
    this.store.pipe(select(getGlobalSettings)).pipe(distinctUntilChanged(), takeUntil(this.destroyed$)).subscribe((res) => {
      if (res) {
        this.globalSettings = res;
      }
    })

    if (this.modalData.event?.slots?.length) {
      this.modalData.event.slots = this.mapSlotsWithBreaks()

      this.selectSlotDate(this.modalData.event.slots?.[0])
    }

    // SAN-792/BRD FR-3.2: the backend now returns every live application
    // this startup has for this event (not just one) -- copied locally so
    // a fresh booking/withdrawal can update it immediately without waiting
    // on a refetch of modalData.event itself.
    this.myApplications = Array.isArray(this.modalData.event?.myApplications)
      ? [...this.modalData.event.myApplications]
      : [];

    // DEV-ONLY TEST SCAFFOLDING -- remove once sc-saas-backend's SAN-792
    // fields (myApplications/applicationCap/maxSlotsPerStartup) are actually
    // deployed to every environment this is tested against. Opt-in via
    // "?mockOneToOne=1" on the page URL so it can never affect a real user
    // by accident, and additionally gated on !environment.production so it
    // is inert even if that query param leaks into a shared/prod link.
    // Simulates: a per-slot cap already reached by OTHER startups (slot 0),
    // and this startup already holding one pending application of its own
    // (slot 1) -- lets the cap/limit/withdraw UI be exercised end-to-end
    // against a live event without waiting on that deploy.
    if (!environment.production && this.isDevMockOneToOneRequested()) {
      this.applyDevMockOneToOneData();
    }

    this.globalService.getEventQuestions(this.modalData.event.uuid).subscribe({
      next: (response: any) => {
        this.eventQuestionsList = response || [];
      },
      // SAN-768/BRD: previously had no error handler -- eventQuestionsList
      // stayed `undefined` on a failed fetch, which would throw inside
      // handleBook()'s `this.eventQuestionsList.length` check the moment the
      // Apply CTA was used. Default to the plain-agenda flow (no custom
      // questions) instead of leaving Apply broken, and say so.
      error: () => {
        this.eventQuestionsList = [];
        this.eventQuestionsLoadError = true;
      }
    })
  }

  // DEV-ONLY TEST SCAFFOLDING -- see the ngOnInit call site above for why
  // this exists and when to delete it.
  private isDevMockOneToOneRequested(): boolean {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('mockOneToOne') === '1';
  }

  private applyDevMockOneToOneData(): void {
    const event = this.modalData.event;
    event.approvalMode = event.approvalMode || 'manual';
    event.applicationCap = event.applicationCap ?? 3;
    event.maxSlotsPerStartup = event.maxSlotsPerStartup ?? 2;

    const firstDateEntry = event.slots?.[0];
    const realSlots = (firstDateEntry?.slots || []).filter((s) => !s?.break);
    if (realSlots.length < 2) return;

    // Slot A: closed as if the per-slot cap (3) was already reached by
    // OTHER startups -- this startup has no application here.
    realSlots[0].booked = true;

    // Slot B: this startup's own live (pending) application.
    realSlots[1].booked = false;
    this.myApplications = [
      {
        uuid: 'dev-mock-application-1',
        date: firstDateEntry.date,
        timeFrom: realSlots[1].timeFrom,
        timeTo: realSlots[1].timeTo,
        approvalStatus: 'pending_moderation',
      },
    ];
  }

  mapSlotsWithBreaks() {
    const newSlots = this.modalData.event.slots.map((e) => {
      const originalSlots = e.slots;
      const breakSlots = e.breakSlots.flatMap((breakSlot) =>
        breakSlot.slots.map((slot) => ({ ...slot, title: breakSlot.title }))
      );


      let updatedSlots = originalSlots.map((slot) => {
        const matchingBreak = breakSlots.find(
          (breakSlot) =>
            slot.timeFrom >= breakSlot.timeFrom && slot.timeTo <= breakSlot.timeTo
        );
        return {
          ...slot,
          break: matchingBreak ? { title: matchingBreak.title } : null,
        };
      });


      updatedSlots = this.mergeConsecutiveBreaks(updatedSlots)
      return {
        ...e,
        slots: updatedSlots,
      };
    });

    return newSlots;
  }
  mergeConsecutiveBreaks(slots) {
    const mergedSlots = [];
    let tempSlot = null;

    slots.forEach(slot => {
      if (slot.break) {

        if (tempSlot && tempSlot.break.title === slot.break.title) {
          tempSlot.timeTo = slot.timeTo; // Extend the timeTo of the break
        } else {

          if (tempSlot) {
            mergedSlots.push(tempSlot);
          }
          tempSlot = { ...slot };
        }
      } else {

        if (tempSlot) {
          mergedSlots.push(tempSlot);
          tempSlot = null;
        }
        mergedSlots.push(slot);
      }
    });

    if (tempSlot) {
      mergedSlots.push(tempSlot);
    }

    return mergedSlots;
  }

  ngOnChanges(changes: SimpleChanges): void {
  }


  get meetingUrl() {
    const event: any = this.modalData.event
    if (event.meetingType === 'job-interview') {
      const link = this.router.serializeUrl(this.router.createUrlTree(['/job-interview/', event.uuid]));
      return window.location.origin + link
    }
    // Hub/Spoke VideoSDK domain-whitelist gap: only the Hub's own domain can
    // ever be whitelisted with VideoSDK, so a spoke visitor's meeting link
    // must resolve to the Hub's own origin -- see MeetingService.getMeetingUrl().
    const link = this.router.serializeUrl(this.router.createUrlTree(['/meetings/meeting/' + event.uuid]));
    return this.meetingService.getMeetingUrl(link)
  }
  meetingDetail(event) {
    this.closeModal();
    if (event.meetingType === 'job-interview') {
      const link = this.router.serializeUrl(this.router.createUrlTree(['/job-interview/', event.uuid]));
      window.open(link, '_blank');
      return;
    }
    const link = this.router.serializeUrl(this.router.createUrlTree(['/meetings/meeting/' + event.uuid]));
    window.open(this.meetingService.getMeetingUrl(link), '_blank');
  }


  closeModal(): void {
    this.modalInst.close({});
  }

  // SAN-973: slot start (date + timeFrom, in the event's own timezone, same
  // anchoring as formatSlotTime()) is before now.
  isSlotInPast(slot: any, date: string): boolean {
    const anchorDate = date || this.modalData?.event?.date;
    if (!anchorDate || !slot?.timeFrom) return false;
    return moment.tz(`${anchorDate} ${slot.timeFrom}`, 'YYYY-MM-DD HH:mm:ss', this.eventTimeZone)
      .isBefore(moment());
  }

  get isInPast() {
    return moment(this.modalData?.event?.date).isBefore(moment(), 'date')
  }

  /* Admin's "For stakeholders" Privacy Setting restricts an event to
     specific account types (allowedUserTypesToBook). An empty/missing
     list means no restriction. Mirrors the authoritative check the
     backend now enforces in EventsService.attendEvent() -- this is UX
     only, to stop showing Apply to someone who'll be rejected anyway. */
  get isStakeholderTypeAllowed(): boolean {
    const allowedTypes = this.modalData?.event?.allowedUserTypesToBook;
    if (!Array.isArray(allowedTypes) || allowedTypes.length === 0) {
      return true;
    }
    return allowedTypes.includes(this.profileDetails?.accountType);
  }

  /* SAN-887: same shape as isStakeholderTypeAllowed above, for the sibling
     admin restriction (allowedUsersProfileStatuses). Reads
     'profile-approval-status' from localStorage -- IProfileDetails doesn't
     carry approvalStatus (it lives on the per-stakeholder-type
     ProfileCompleteness models instead), but ProfileService already mirrors
     it there on every completeness update, which is the same value the
     backend's EventsService.attendEvent() authoritatively checks. */
  get isProfileStatusAllowed(): boolean {
    const allowedStatuses = this.modalData?.event?.allowedUsersProfileStatuses;
    if (!Array.isArray(allowedStatuses) || allowedStatuses.length === 0) {
      return true;
    }
    return allowedStatuses.includes(localStorage.getItem('profile-approval-status'));
  }

  tConvert(time, date) {
    return time
  }

  get eventDate() {
    return new Date(this.modalData.event.date)
  }


  formatTo24Hour(timeAMPM) {
    const time24Hour = moment(timeAMPM, 'h:mm A').format('HH:mm');
    return time24Hour
  }

  // selectResponse(item) {
  //   const event: any = this.modalData.event

  //   this.selectedResponse = item;

  //   this.globalService.eventResponse(event.id, item.value).subscribe()
  // }

  async handleBook(slot, date) {
    // console.log('this.modal', this.modalData)
    // return

    // A fresh attempt clears any error left over from a previous one, so a
    // stale "limit reached" banner doesn't linger next to a slot the user
    // is now trying for the first time.
    this.applyErrorMessage = null;

    // SAN-973: the modal may have stayed open past this slot's start time.
    if (this.isSlotInPast(slot, date)) {
      this.applyErrorMessage = 'This slot has already started and can no longer be booked.';
      return;
    }

    // SAN-716: the slot list shown here comes from whatever was already
    // fetched when the calendar/modal opened -- it's never refetched before
    // a submit, so two users can both be looking at the same "available"
    // slot at once. Re-check right before booking to close most of that
    // window (the backend is still the real source of truth once SAN-714
    // lands; this only improves the common case).
    const stillAvailable = await this.confirmSlotStillAvailable(this.modalData.event.uuid, date, slot.timeFrom);
    if (!stillAvailable) {
      this.applyErrorSeverity = 'danger';
      this.applyErrorMessage = 'This slot was just booked by someone else. Please choose another.';
      this.toastService.showToast(this.applyErrorMessage, 'error');
      return;
    }

if(this.eventQuestionsList.length){
  this.showChildComponent=true;
  this.bookingData = {
    event: this.modalData.event,
    date:date,
    SlotTime: slot.timeFrom,
    timeFrom: slot.timeFrom.split(":").slice(0, 2).join(':'),
    timeTo: slot.timeTo.split(":").slice(0, 2).join(':'),
    attending:'attending',
    message:"Slot was successfully booked."
  }
}
else{

    const { value: text, isConfirmed } = await Swal.fire({
      input: "textarea",
      title: "<p class='fs-2 mt-4'>Agenda <span class='text-red'>*</span></p>",
      inputPlaceholder: "Share the topic you would like to discuss",
      inputAttributes: {
        "aria-label": "Share the topic you would like to discuss"
      },
      showCancelButton: true,
      confirmButtonColor: 'var(--color-theme)',
      cancelButtonColor: '#7D8185',
      confirmButtonText: 'Submit'
    });
    if(isConfirmed) {
      if (text) {
        this.bookingLoading = true;
        const event: any = this.modalData.event
        console.log('event', event)
        this.bookingLoadingForSlotTimeFrom = slot.timeFrom;
        const timeFrom = slot.timeFrom.split(":").slice(0, 2).join(':')
        const timeTo = slot.timeTo.split(":").slice(0, 2).join(':')
        this.globalService.bookEventSlot(event.uuid, timeFrom, timeTo, 'attending', text, date, "Slot was successfully booked.").subscribe({
          next: (res) => {
            this.bookingLoading = false;
            // SAN-792: appended, not replaced -- a startup may hold more
            // than one live application for this event (up to
            // maxSlotsPerStartup), so a new booking adds to the list
            // instead of overwriting whatever was already there.
            if (res) {
              this.myApplications = [...this.myApplications, res];
            }
          },
          error: (fault) => {
            // Previously had no error handler at all -- bookingLoading was
            // left true forever (spinner stuck) and the user saw no
            // feedback on a failed booking (e.g. a slot-conflict rejection).
            this.bookingLoading = false;
            // SAN-789/BRD FR-3.2: a generic "slot just taken" message is
            // actively misleading for the per-startup slot-limit case (the
            // slot itself may still be wide open -- the startup has simply
            // used up its allowed attempts for this event) -- surface the
            // backend's actual message when one is present, falling back to
            // the generic slot-conflict copy only when it isn't.
            const backendMessage = fault?.error?.message;
            this.applyErrorMessage = backendMessage || 'This slot could not be booked — it may have just been taken. Please choose another.';
            // A 400 here is an ordinary business-rule block the startup can
            // act on (e.g. their own per-event slot limit, or a slot outside
            // bookable hours) -- shown as a warning, not a failure. A 409
            // (someone else won the slot) or anything else is a genuine
            // conflict/failure, shown as danger. This reads the HTTP status
            // the backend already sets rather than matching its message
            // text, so it keeps working if the copy changes.
            this.applyErrorSeverity = fault?.status === 400 ? 'warning' : 'danger';
            this.toastService.showToast(this.applyErrorMessage, this.applyErrorSeverity === 'warning' ? 'warning' : 'error');
          }
        });

      }else{
        this.toastService.showToast('Agenda is required to book a slot', 'error')
      }
    }
  }

  }

  /* SAN-716: refetch the event's current slot state (same call the calendar
     page already makes on load) and report whether the given slot is still
     unbooked. Refreshes the modal's own slot list/selection with the fresh
     data as a side effect, so a taken slot greys out immediately even if
     the user doesn't proceed with a different one. */
  private confirmSlotStillAvailable(eventUuid: string, date: string, timeFrom: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.globalService.getPlatformEvents().subscribe({
        next: (events: any[]) => {
          const freshEvent = (events || []).find((e) => e.uuid === eventUuid);
          if (!freshEvent?.slots?.length) {
            resolve(true);
            return;
          }

          const freshDateEntry = freshEvent.slots.find((s) => s.date === date);
          const freshSlot = freshDateEntry?.slots?.find((s) => s.timeFrom === timeFrom);

          this.modalData.event.slots = freshEvent.slots;
          this.modalData.event.slots = this.mapSlotsWithBreaks();
          const refreshedDateEntry = this.modalData.event.slots.find((s) => s.date === date);
          if (refreshedDateEntry) {
            this.selectSlotDate(refreshedDateEntry);
          }

          resolve(!freshSlot || !freshSlot.booked);
        },
        // A refetch failure shouldn't block booking on its own -- fall back
        // to letting the submit proceed and let the backend be the guard.
        error: () => resolve(true)
      });
    });
  }

  get speakerProfileLink() {
    if (this.modalData?.event?.speakerCompanyDetails) {
      return this.profileService.getProfileLink(
        this.modalData?.event?.speakerCompanyDetails?.uuid,
        this.modalData?.event?.speakerCompanyDetails?.name,
        this.modalData?.event?.speakerDetails?.accountType
      )
    }
    return ''
  }

  selectedSlotObj = null
  selectSlotDate(slotObj) {
    this.selectedSlotObj = slotObj
  }

  // A slot's own `date`/`timeFrom` come from the calendar's plain
  // "YYYY-MM-DD"/"HH:mm:ss" listing strings, but an application record's
  // `date` comes back from a TypeORM `date` column (serialized as a full
  // ISO timestamp, e.g. "2026-09-16T00:00:00.000Z") and its `timeFrom` is
  // whatever was actually submitted at booking time (handleBook() truncates
  // to "HH:mm" before sending) -- a direct `===` between the two therefore
  // never matches even for the exact same slot. Compare only the parts that
  // are guaranteed comparable: the first 10 chars for date, first 5 for time.
  private toDateKey(value: string | Date): string {
    if (!value) return '';
    return (value instanceof Date ? value.toISOString() : String(value)).slice(0, 10);
  }

  private toTimeKey(value: string): string {
    return (value || '').slice(0, 5);
  }

  // Any status (not just live) -- used to show this exact slot's own
  // history (including a past Rejected) directly on its row.
  getMyApplicationForSlot(date: string, timeFrom: string) {
    const dateKey = this.toDateKey(date);
    const timeKey = this.toTimeKey(timeFrom);
    return this.myApplications.find(
      (app) => this.toDateKey(app.date) === dateKey && this.toTimeKey(app.timeFrom) === timeKey
    );
  }

  // Single source of truth for how one slot row renders, in priority order:
  // 1) this startup's own application for this exact slot (whatever its
  //    status -- Applied/Approved/Rejected all take priority over the
  //    slot's general open/closed state, since it's more specific/relevant
  //    to this viewer than either);
  // 2) the startup's own event-level slot limit, which blocks applying to
  //    ANY further slot regardless of this one's own capacity;
  // 3) this slot's own open/closed state (cap reached / already booked).
  getSlotView(slot: any, date: string): { kind: 'mine-pending' | 'mine-approved' | 'mine-rejected' | 'past' | 'not-eligible' | 'not-eligible-status' | 'limit-locked' | 'full' | 'open'; myApp?: any } {
    const myApp = this.getMyApplicationForSlot(date, slot.timeFrom);
    if (myApp?.approvalStatus === 'pending_moderation') return { kind: 'mine-pending', myApp };
    if (myApp?.approvalStatus === 'approved') return { kind: 'mine-approved', myApp };
    if (myApp?.approvalStatus === 'rejected') return { kind: 'mine-rejected', myApp };
    // SAN-973: a slot whose start time has already passed can't be booked.
    if (this.isSlotInPast(slot, date)) return { kind: 'past' };
    // Admin's "For stakeholders" Privacy Setting -- mirrors the
    // authoritative backend check in EventsService.attendEvent(). UX only:
    // stops offering Book on a slot the backend would reject anyway.
    if (!this.isStakeholderTypeAllowed) return { kind: 'not-eligible' };
    // SAN-887: sibling profile-status restriction, same backend method.
    if (!this.isProfileStatusAllowed) return { kind: 'not-eligible-status' };
    if (this.hasReachedSlotLimit) return { kind: 'limit-locked' };
    if (slot?.booked) return { kind: 'full' };
    return { kind: 'open' };
  }

  // BRD UX state 5 (per-startup application limit): mirrors the backend's
  // own countLiveSlotLimitApplications() (every application except a
  // Reversed one -- Withdrawn is hard-deleted so it's never in this list at
  // all) closely enough for a proactive UI check. The one edge case this
  // doesn't special-case is a Reversed-then-restored application, which the
  // backend excludes from the count but this treats as an ordinary live one
  // -- rare enough (an admin walking back an approval) that the backend's
  // own enforcement on submit remains the real guard either way.
  get liveApplicationsCount(): number {
    return this.myApplications.length;
  }

  get hasReachedSlotLimit(): boolean {
    return this.hasMaxSlotsPerStartup && this.liveApplicationsCount >= this.modalData?.event?.maxSlotsPerStartup;
  }

  // BRD UX state 8 (withdraw confirmation): asks first, since this can't be
  // undone by the startup themselves once confirmed -- mirrors the app's
  // existing confirm-before-destructive-action pattern (ToastAlertService's
  // showLeaveConfirm) rather than introducing a new one.
  async withdrawApplication(app: any): Promise<void> {
    if (!app?.uuid || app.approvalStatus !== 'pending_moderation') {
      return;
    }
    const { isConfirmed } = await Swal.fire({
      icon: 'warning',
      title: 'Withdraw Application',
      text: 'Are you sure you want to withdraw your application for this slot? You can apply again if the slot is still available.',
      showCancelButton: true,
      confirmButtonColor: 'var(--color-theme)',
      cancelButtonColor: '#7D8185',
      confirmButtonText: 'Withdraw',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });
    if (!isConfirmed) {
      return;
    }

    // DEV-ONLY TEST SCAFFOLDING: a mock application has no real backend row
    // to withdraw -- simulate success locally instead of calling the API
    // with a fake uuid. See applyDevMockOneToOneData()/its ngOnInit call
    // site for context; remove together with that scaffolding.
    if (String(app.uuid).startsWith('dev-mock-')) {
      this.myApplications = this.myApplications.filter((a) => a.uuid !== app.uuid);
      return;
    }

    this.withdrawLoadingUuid = app.uuid;
    this.globalService.withdrawEventSlot(this.modalData.event.uuid, app.uuid).subscribe({
      next: () => {
        this.withdrawLoadingUuid = null;
        // SAN-772/BRD FR-1.4: a clean void, not a rejection -- withdrawing
        // removes this one application from the list; any of the startup's
        // OTHER live applications for this event are untouched.
        this.myApplications = this.myApplications.filter((a) => a.uuid !== app.uuid);
      },
      error: () => {
        this.withdrawLoadingUuid = null;
        this.toastService.showToast('Could not withdraw your application. Please try again.', 'error');
      }
    });
  }

  get slots() {
    return (this.modalData?.event?.slots || []).filter(e => !e.booked)
  }

  get hasAnyBookableSlot() {
    return (this.modalData?.event?.slots || []).some((slotObj) =>
      (slotObj?.slots || []).some((slot) => !slot?.booked && !slot?.break)
    );
  }

  // BRD UX state 2 (closed/full slot): true as soon as the date has any real
  // (non-break) slot at all, open or closed, so a fully-booked date still
  // renders its slot list (each row showing why it can't be applied for)
  // instead of collapsing to "No slots available for this date", which reads
  // as a scheduling gap rather than a capacity one.
  get hasAnySlotForSelectedDate() {
    return (this.selectedSlotObj?.slots || []).some((slot) => !slot?.break);
  }

  formatEnumStringForUI(input) {
    return formatEnumStringForUI(input)
  }

  formatEventTypeLabel(eventType) {
    return formatEventTypeLabel(eventType)
  }

  // SAN-768/BRD FR-2.1/FR-3.1/FR-3.2: conditions driven by the event's own
  // configured Approval Mode + caps (now returned by the backend, SAN-790).
  get isAutomaticApprovalMode(): boolean {
    return this.modalData?.event?.approvalMode === 'automatic';
  }

  get hasApplicationCap(): boolean {
    return this.modalData?.event?.applicationCap != null;
  }

  get hasMaxSlotsPerStartup(): boolean {
    return this.modalData?.event?.maxSlotsPerStartup != null;
  }

  // BRD UX state 2 (closed/full slot): the listing API only returns a
  // per-slot `booked` boolean, not *why* -- it doesn't say whether that's an
  // Automatic-mode instant booking or a Manual-mode cap already reached, so
  // this can only state the general reason implied by the event's own
  // configured Approval Mode, not the specific slot's history. Deliberately
  // does not say "rejected" or "application rejected" for a closed slot --
  // a full/closed slot was never an application outcome for this viewer.
  get closedSlotReasonLabel(): string {
    return this.isAutomaticApprovalMode ? 'Already booked' : 'Full';
  }

  get closedSlotReasonTooltip(): string {
    return this.isAutomaticApprovalMode
      ? 'This slot was already booked by another startup.'
      : 'This slot is no longer accepting applications — it has either reached its application limit or already has a confirmed booking.';
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }


  get mainSpeaker(){
    const speaker = this.modalData?.event?.speakerDetails;
    const speakerCompanyDetails = this.modalData.event?.speakerCompanyDetails;
    if (!speaker) {
      return {}
    }
    return {
        "bio": this.modalData?.event.speakerBio,
        "name": speaker?.name,
        "email": "",
        "mobile": null,
        "imageKey": null,
        "linkedin":this.modalData?.event.speakerLinkedin,
        "existingImage": speakerCompanyDetails?.logo || speaker.avatar,
        "speaker_image": speakerCompanyDetails?.logo || speaker.avatar,

    }
  }

  handleMainSpeakerClick() {
    const link = this.profileService.getProfileLink(this.modalData.event.speakerCompanyDetails.uuid, this.modalData.event.speakerCompanyDetails.name, this.modalData.event.speakerDetails.accountType)
    
    // this.router.navigateByUrl(link);
    const url = this.router.serializeUrl(
      this.router.createUrlTree([link])
    );
    window.open(url, '_blank');
    // this.closeModal();
  }
  closeChild() {
    this.selectedResponse = { value: this.modalData.event?.checkIfResponseExists?.responseAction};
    this.showChildComponent = false;
  }
  bookingResponseUpdate(eventData: any): void {
    if (eventData) {
      this.myApplications = [...this.myApplications, eventData];
    }
    this.selectedResponse = { value: eventData.responseAction };
  }
}
